import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Share2, MessageCircle, Copy, Image as ImageIcon, Send } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function ShareCard({ leagueId, leagueName, open: openProp, onOpenChange: onOpenChangeProp, hideTrigger, heading, subheading }) {
  const { t } = useI18n();
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internalOpen;
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const ensureToken = async () => {
    if (token) return token;
    const { data } = await api.get(`/leagues/${leagueId}/share-link`, { params: { base: BACKEND } });
    setToken(data.token);
    return data.token;
  };

  const onOpenChange = async (o) => {
    if (controlled) { onOpenChangeProp?.(o); } else { setInternalOpen(o); }
    if (o) {
      setLoading(true);
      try { await ensureToken(); } catch { toast.error(t("share.error") || "Error"); }
      finally { setLoading(false); }
    }
  };

  const shareUrl = token ? `${BACKEND}/api/share/${token}` : "";
  const imgUrl = token ? `${BACKEND}/api/share/card/${token}.png` : "";
  const caption = t("share.caption", { league: leagueName || "" });

  const openWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(caption + " " + shareUrl)}`, "_blank");
  const openX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success(t("share.copied")); }
    catch { toast.error(t("pl.error")); }
  };
  const viewImage = () => window.open(imgUrl, "_blank");
  const nativeShare = async () => {
    try {
      await navigator.share({ title: "GAFFER", text: caption, url: shareUrl });
    } catch { /* user cancelled */ }
  };
  const canNative = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" data-testid="share-card-btn">
            <Share2 size={15} className="me-2" /> {t("share.button")}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="bg-card border-2 border-foreground rounded-none max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase">{heading || t("share.title")}</DialogTitle>
          <DialogDescription className="font-data text-xs">{subheading || t("share.desc")}</DialogDescription>
        </DialogHeader>

        <div className="border-2 border-border bg-background">
          {imgUrl
            ? <img src={imgUrl} alt="predicted table" className="w-full" data-testid="share-preview-img" />
            : <div className="h-40 grid place-items-center text-muted-foreground font-data text-sm">{loading ? "…" : ""}</div>}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button onClick={openWhatsApp} disabled={!token} data-testid="share-whatsapp-btn"
            className="bg-[hsl(145_63%_42%)] text-white border-2 border-transparent hover:brightness-110">
            <MessageCircle size={16} className="me-2" /> {t("share.whatsapp")}
          </Button>
          <Button onClick={openX} disabled={!token} data-testid="share-x-btn"
            className="bg-foreground text-background border-2 border-transparent hover:brightness-110">
            {t("share.x")}
          </Button>
          <Button onClick={copyLink} disabled={!token} variant="outline" data-testid="share-copy-btn">
            <Copy size={15} className="me-2" /> {t("share.copy")}
          </Button>
          <Button onClick={viewImage} disabled={!token} variant="outline" data-testid="share-image-btn">
            <ImageIcon size={15} className="me-2" /> {t("share.download")}
          </Button>
        </div>

        {canNative && (
          <Button onClick={nativeShare} disabled={!token} variant="ghost" data-testid="share-native-btn" className="w-full">
            <Send size={15} className="me-2" /> {t("share.native")}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
